import '@/assets/styles/foreign-amount-grid.scss'
import { ForeignAmountsCountryPeriod } from '@/lib/api/company/timesheet/foreignAmounts'
import moment from 'moment'
import { duration as momentDuration } from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import * as React from 'react'

import { DATE_FORMAT, TIME_FORMAT } from 'app/constants/date'
import { CountryModel } from '@/app/models/timesheet/countryModel'
import FlexView from '@/modules/shared/components/FlexView'
import { TitledValue } from 'app/components/timesheet/titledValue'
import { PartDetailsModel } from '@/app/models/timesheet/amountsPreviewModel'
import { formatTimespan, formatDuration } from 'app/common/dateHelper'

import { AmountsTable } from './AmountsTable'

type Props = {
  className?: string
  data: ForeignAmountsCountryPeriod
}

type TranslateFn = (str: string) => string

type TableProps = {
  className?: string
  children: React.ReactNode
}

export const ForeignAmountSingleCountry = ({ data }: Props) => {
  const { t } = useLocalize()

  const displayContent = true
  const translate = t
  const amounts = data
  const startDate = moment(amounts.periodStartDate).format(DATE_FORMAT)
  const endDate = moment(amounts.periodEndDate).format(DATE_FORMAT)

  const sumOfDrivingOnCountryTerritory = amounts.workOnCountryTerritory.reduce(
    (prev, cur) => {
      return (
        momentDuration(prev).asMilliseconds() +
        momentDuration(cur.drivingDuration).asMilliseconds()
      )
    },
    0,
  )

  const sumOfWorkOnCountryTerritory = amounts.workOnCountryTerritory.reduce(
    (prev, cur) => {
      return (
        momentDuration(prev).asMilliseconds() +
        momentDuration(cur.workDuration).asMilliseconds()
      )
    },
    0,
  )

  const sumOfSummaryOnCountryTerritory = amounts.workOnCountryTerritory.reduce(
    (prev, cur) => {
      return (
        momentDuration(prev).asMilliseconds() +
        momentDuration(cur.summary).asMilliseconds()
      )
    },
    0,
  )

  const sumOfAvailabilityOnCountryTerritory =
    amounts.workOnCountryTerritory.reduce((prev, cur) => {
      return (
        momentDuration(prev).asMilliseconds() +
        momentDuration(cur.availabilityDuration).asMilliseconds()
      )
    }, 0)

  const sumOfWorkTimeOnCountryTerritory = amounts.workOnCountryTerritory.reduce(
    (prev, cur) => {
      return (
        momentDuration(prev).asMilliseconds() +
        momentDuration(cur.payableWorkTime).asMilliseconds()
      )
    },
    0,
  )

  const hideExpiredInfo = true // hide percentage details, details table,

  return (
    <div data-test-id="foreign-amount-single-country">
      <div data-test-id="foreign-amount-single-country-base">
        {/* Zawija się na wąskim ekranie zamiast rozpychać tabelę. */}
        <div className="tw-mb-[15px] tw-ml-[10px] tw-flex tw-flex-wrap tw-items-center tw-gap-x-[90px] tw-gap-y-3 tw-whitespace-nowrap">
          <TitledValue
            title={translate('foreignAmounts.period-title')}
            text={`${startDate} - ${endDate}`}
          />
          <TitledValue
            title={translate('foreignAmounts.country')}
            text={amounts.country.name}
          />
          {amounts.shouldDisplayDriverWorkYears && (
            <TitledValue
              title={translate('foreignAmounts.driverWorkYears')}
              text={amounts.driverWorkYears.toString()}
            />
          )}
          <TitledValue
            title={translate('foreignAmounts.countryCurrencyExchangeRate')}
            text={amounts.countryCurrencyExchangeRate.toString()}
          />
        </div>
      </div>

      <div data-test-id="foreign-amount-single-country-ingridients">
        <AmountsTable>
          <thead>
            {drawExtendedRow(
              translate('amountsPreview.partName').toUpperCase(),
              translate('amountsPreview.partHours').toUpperCase(),
              translate('amountsPreview.partRate').toUpperCase(),
              translate('amountsPreview.partAmount').toUpperCase(),
              true,
              '#F1F6FA',
            )}
          </thead>
          <tbody>
            {/* Kolejność jak w WPF (DriverTimesheetForeignAmountsView). */}
            {[
              amounts.amountsForForeignCountryMinimalPayment,
              amounts.amountsForForeignCountryOverhoursMinimalPayment,
              amounts.amountsForForeignCountryWeekOverhours,
              amounts.amountsForForeignCountryExtraAddtion,
              amounts.amountsForForeignCountryNightWorkMinimalPayment,
              amounts.amountsForeignCountryAdditionForWorkOnHoliday,
              amounts.amountsForForeignCountryPaymentFromContract,
              amounts.amountsForForeignCountryMinimalPaymentFromContract,
              amounts.amountsForForeignCountryAdditionForOverhours50,
              amounts.amountsForForeignCountryAdditionForOverhours100,
              amounts.amountsForForeignCountryWorkOnHoliday,
              amounts.amountsForForeignCountryDuty50,
              amounts.amountsForForeignCountryDuty100,
              amounts.amountsForForeignCountryNightWork,
              amounts.amountsForForeignCountryAdditionForDriving,
              amounts.additionalAmountsForOvertimeLumpSum,
              amounts.additionalAmountsForDutyLumpSum,
              amounts.additionalAmountsForNightWorkLumpSum,
              amounts.additionalAmountsForAllRatesLumpSum,
              amounts.additionalAmountsForLumpSumSettlements,
              amounts.additionalAmountsForBonus,
              amounts.additionalAmountsForReward,
              amounts.amountsForForeignCountryAllowance,
              amounts.amountsForForeignCountryAccomodation,
              amounts.additionalAmountsForEuWork,
            ].map((parts, i) => (
              <React.Fragment key={i}>{renderParts(parts)}</React.Fragment>
            ))}
          </tbody>
          <tfoot>
            {drawExtendedRow(
              translate('foreignAmounts.alignment'),
              '',
              '',
              `${amounts.alignmentAmountToDisplay} / ${amounts.amountInHomelandCurrencyToDisplay}`,
              true,
              '#F1F6FA',
            )}
          </tfoot>
        </AmountsTable>
      </div>

      <div data-test-id="foreign-amount-single-country-time-in-the-country">
        <AmountsTable
          title={translate('foreignAmounts.timeInCountry')}
          className="tw-my-6"
        >
          <thead>
            {drawRow(
              translate('foreignAmounts.arriveDate').toUpperCase(),
              translate('foreignAmounts.leaveDate').toUpperCase(),
              true,
              '#F1F6FA',
            )}
          </thead>
          <tbody>
            {amounts.timesOnCountryTerritory.map((visit, i) => {
              return (
                <React.Fragment key={i}>
                  {drawRow(
                    moment(visit.arriveDate).format(TIME_FORMAT),
                    moment(visit.leaveDate).format(TIME_FORMAT),
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </AmountsTable>
      </div>

      <div data-test-id="foreign-amount-single-country-detailedSettlement">
        {Boolean(amounts.allowanceParts.length > 0) && (
          <AmountsTable
            title={translate('foreignAmounts.detailedSettlement')}
            className="tw-my-6"
          >
            <thead>
              {drawDetailsRow(
                translate('foreignAmounts.name').toUpperCase(),
                translate('foreignAmounts.quantity').toUpperCase(),
                translate('foreignAmounts.rate').toUpperCase(),
                translate('foreignAmounts.amount').toUpperCase(),
                translate('foreignAmounts.percentagePerPeriod').toUpperCase(),
                translate('foreignAmounts.calculatedAmount').toUpperCase(),
                translate(
                  'foreignAmounts.amountInCountryCurrency',
                ).toUpperCase(),
                true,
                '#F1F6FA',
              )}
            </thead>
            <tbody>
              {amounts.allowanceParts.map((allowancePart, i) => {
                return (
                  <div key={i}>
                    {drawDetailsRow(
                      allowancePart.eventName,
                      allowancePart.allowanceSize,
                      allowancePart.rate,
                      allowancePart.amount,
                      allowancePart.periodRatio,
                      allowancePart.amountInHomeCurrency,
                      allowancePart.amountInCountryCurrency,
                    )}
                  </div>
                )
              })}
            </tbody>
            <tfoot>
              {drawDetailsRow(
                translate('foreignAmounts.summary'),
                '',
                '',
                '',
                '',
                '',
                amounts.sumOfAllowanceParts,
                true,
                '#F1F6FA',
              )}
            </tfoot>
          </AmountsTable>
        )}
      </div>

      <div data-test-id="foreign-amount-single-country-no-17">
        {amounts.country.nationId != 17 && (
          <AmountsTable title={translate('foreignAmounts.workInCountry')}>
            <thead>
              {drawWorkInCountryRow(
                translate('foreignAmounts.day').toUpperCase(),
                translate('foreignAmounts.driving').toUpperCase(),
                translate('foreignAmounts.otherWork').toUpperCase(),
                translate('foreignAmounts.summary').toUpperCase(),
                true,
                '#F1F6FA',
              )}
            </thead>
            <tbody>
              {amounts.workOnCountryTerritory.map((w, i) => {
                return (
                  <React.Fragment key={i}>
                    {drawWorkInCountryRow(
                      moment(w.dayDate).format(DATE_FORMAT),
                      formatTimespan(w.drivingDuration),
                      formatTimespan(w.workDuration),
                      formatTimespan(w.summary),
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
            <tfoot>
              {drawWorkInCountryRow(
                translate('foreignAmounts.summary'),
                formatDuration(
                  momentDuration(
                    sumOfDrivingOnCountryTerritory,
                    'milliseconds',
                  ),
                ),
                formatDuration(
                  momentDuration(sumOfWorkOnCountryTerritory, 'milliseconds'),
                ),
                formatDuration(
                  momentDuration(
                    sumOfSummaryOnCountryTerritory,
                    'milliseconds',
                  ),
                ),
                true,
                '#F1F6FA',
              )}
            </tfoot>
          </AmountsTable>
        )}
        <div style={{ marginTop: 5 }}>
          {renderWorkTimeSourceInfo(
            amounts.country as any as CountryModel,
            amounts.workTimeSource,
            t,
          )}
        </div>
      </div>

      <div data-test-id="foreign-amount-single-country-17">
        {amounts.country.nationId == 17 && (
          <AmountsTable title={translate('foreignAmounts.workInCountry')}>
            <thead>
              {drawWorkWithAvailabilityInCountryRow(
                translate('foreignAmounts.day').toUpperCase(),
                translate('foreignAmounts.driving').toUpperCase(),
                translate('foreignAmounts.otherWork').toUpperCase(),
                translate('foreignAmounts.availability').toUpperCase(),
                translate('foreignAmounts.workTime').toUpperCase(),
                true,
                '#F1F6FA',
              )}
            </thead>
            <tbody>
              {amounts.workOnCountryTerritory.map((w, i) => {
                return (
                  <React.Fragment key={i}>
                    {drawWorkWithAvailabilityInCountryRow(
                      moment(w.dayDate).format(DATE_FORMAT),
                      formatTimespan(w.drivingDuration),
                      formatTimespan(w.workDuration),
                      formatTimespan(w.availabilityDuration),
                      formatTimespan(w.payableWorkTime),
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
            <tfoot>
              {drawWorkWithAvailabilityInCountryRow(
                translate('foreignAmounts.summary'),
                formatDuration(
                  momentDuration(
                    sumOfDrivingOnCountryTerritory,
                    'milliseconds',
                  ),
                ),
                formatDuration(
                  momentDuration(sumOfWorkOnCountryTerritory, 'milliseconds'),
                ),
                formatDuration(
                  momentDuration(
                    sumOfAvailabilityOnCountryTerritory,
                    'milliseconds',
                  ),
                ),
                formatDuration(
                  momentDuration(
                    sumOfWorkTimeOnCountryTerritory,
                    'milliseconds',
                  ),
                ),
                true,
                '#F1F6FA',
              )}
            </tfoot>
          </AmountsTable>
        )}
        {amounts.country.nationId == 17 && (
          <div style={{ marginTop: 5 }}>
            {renderWorkTimeSourceInfo(
              amounts.country as any as CountryModel,
              amounts.workTimeSource,
              t,
            )}
          </div>
        )}
      </div>

      <div data-test-id="foreign-amount-single-country-accomodationParts">
        {Boolean(amounts.accomodationParts.length > 0) && (
          <AmountsTable
            title={translate('foreignAmounts.detailedSettlement')}
            className="tw-my-6"
          >
            <thead>
              {drawDetailsRow(
                translate('foreignAmounts.name').toUpperCase(),
                translate('foreignAmounts.quantity').toUpperCase(),
                translate('foreignAmounts.rate').toUpperCase(),
                translate('foreignAmounts.amount').toUpperCase(),
                translate('foreignAmounts.lumpSumCountry').toUpperCase(),
                translate('foreignAmounts.calculatedAmount').toUpperCase(),
                translate(
                  'foreignAmounts.amountInCountryCurrency',
                ).toUpperCase(),
                true,
                '#F1F6FA',
              )}
            </thead>
            <tbody>
              {amounts.accomodationParts.map((accomodationPart, i) => {
                return (
                  <div key={i}>
                    {drawDetailsRow(
                      accomodationPart.eventName,
                      accomodationPart.allowanceSize,
                      accomodationPart.rate,
                      accomodationPart.amount,
                      accomodationPart.periodRatio,
                      accomodationPart.amountInHomeCurrency,
                      accomodationPart.amountInCountryCurrency,
                    )}
                  </div>
                )
              })}
            </tbody>
            <tfoot>
              {drawDetailsRow(
                translate('foreignAmounts.summary'),
                '',
                '',
                '',
                '',
                '',
                amounts.sumOfAccomodationParts,
                true,
                '#F1F6FA',
              )}
            </tfoot>
          </AmountsTable>
        )}
      </div>

      <div
        className="tw-mt-2"
        data-test-id="foreign-amount-single-country-percent-details"
      >
        {renderPercentInfo(amounts.country as any as CountryModel, t)}
      </div>
    </div>
  )
}

function renderPercentInfo(country: CountryModel, translate: TranslateFn) {
  let countryName = null
  if (country.nationId == 17) {
    countryName = translate('foreignAmounts.france')
  } else if (country.nationId == 13) {
    countryName = translate('foreignAmounts.germany')
  } else if (country.nationId == 37) {
    countryName = translate('foreignAmounts.norway')
  }

  if (countryName == null) return null

  return (
    <span style={{ fontSize: 11 }}>
      {translate('foreignAmounts.allowancePercentInfo')
        .toString()
        .replace('{0}', countryName)}
    </span>
  )
}

function renderParts(parts: Array<PartDetailsModel> = []) {
  return parts.map((d, i) => {
    return (
      <React.Fragment key={i}>
        {drawExtendedRow(d.eventName, d.hours, d.rates, d.amountToDisplay)}
      </React.Fragment>
    )
  })
}

function drawExtendedRow(
  value1: string,
  value2: string,
  value3: string,
  value4: string,
  bold: boolean = false,
  color: string = '#F8FAFC',
  margin: number = 2,
) {
  const isHeading = color !== '#F8FAFC'
  return isHeading ? (
    <tr
      style={{
        // background: color,
        marginLeft: margin,
        fontWeight: bold ? 500 : 400,
      }}
    >
      <th>{value1}</th>
      <th>{value2}</th>
      <th>{value3}</th>
      <th>{value4}</th>
    </tr>
  ) : (
    <tr
      style={{
        // background: color,
        marginLeft: margin,
        fontWeight: bold ? 500 : 400,
      }}
    >
      <td>{value1}</td>
      <td>{value2}</td>
      <td>{value3}</td>
      <td>{value4}</td>
    </tr>
  )
  return (
    <div
      className="tw-flex tw-flex-wrap -tw-mx-[15px]"
      style={{ marginTop: margin, fontWeight: bold ? 500 : 400 }}
    >
      <div className="fa-col-6" style={{ background: color }}>
        {value1}
      </div>
      <div
        className="fa-col-1"
        style={{ background: color, marginLeft: margin }}
      >
        {value2}
      </div>
      <div className="tw-relative tw-w-full tw-px-[15px] tw-basis-0 tw-grow tw-max-w-full" style={{ background: color, marginLeft: margin }}>
        <FlexView hAlignContent="right" vAlignContent="center">
          {value3}
        </FlexView>
      </div>
      <div className="tw-relative tw-w-full tw-px-[15px] tw-basis-0 tw-grow tw-max-w-full" style={{ background: color, marginLeft: margin }}>
        <FlexView hAlignContent="right" vAlignContent="center">
          {value4}
        </FlexView>
      </div>
    </div>
  )
}

function drawRow(
  value1: string,
  value2: string,
  bold: boolean = false,
  color: string = '#F8FAFC',
  margin: number = 2,
) {
  const isHeading = color !== '#F8FAFC'
  return isHeading ? (
    <tr
      style={{
        // background: color,
        marginLeft: margin,
        fontWeight: bold ? 500 : 400,
      }}
    >
      <th>{value1}</th>
      <th>{value2}</th>
    </tr>
  ) : (
    <tr
      style={{
        // background: color,
        marginLeft: margin,
        fontWeight: bold ? 500 : 400,
      }}
    >
      <td>{value1}</td>
      <td>{value2}</td>
    </tr>
  )
  return (
    <div
      className="tw-flex tw-flex-wrap -tw-mx-[15px]"
      style={{ marginTop: margin, fontWeight: bold ? 500 : 400 }}
    >
      <div className="tw-relative tw-w-full tw-px-[15px] tw-basis-0 tw-grow tw-max-w-full" style={{ background: color }}>
        {value1}
      </div>
      <div className="tw-relative tw-w-full tw-px-[15px] tw-basis-0 tw-grow tw-max-w-full" style={{ background: color, marginLeft: margin }}>
        {value2}
      </div>
    </div>
  )
}

function drawWorkInCountryRow(
  value1: string,
  value2: string,
  value3: string,
  value4: string,
  bold: boolean = false,
  color: string = '#F8FAFC',
  margin: number = 2,
) {
  const isHeading = color !== '#F8FAFC'
  return (
    <tr>
      {renderCell(value1, { textAlign: 'left', heading: isHeading })}
      {renderCell(value2, { textAlign: 'left', heading: isHeading })}
      {renderCell(value3, { textAlign: 'left', heading: isHeading })}
      {renderCell(value4, { textAlign: 'left', heading: isHeading })}
    </tr>
  )

  return (
    <div
      className="tw-flex tw-flex-wrap -tw-mx-[15px]"
      style={{ marginTop: margin, fontWeight: bold ? 500 : 400 }}
    >
      {drawCell(value1, true, color, 0)}
      {drawCell(value2, false, color, margin)}
      {drawCell(value3, false, color, margin)}
      {drawCell(value4, false, color, margin)}
    </div>
  )
}

function drawCell(
  value: string,
  alignLeft: boolean = true,
  color: string = '#F8FAFC',
  margin: number = 2,
  sm: string = null,
) {
  if (sm == null) {
    return (
      <div className="tw-relative tw-w-full tw-px-[15px] tw-basis-0 tw-grow tw-max-w-full" style={{ background: color, marginLeft: margin }}>
        <FlexView
          hAlignContent={alignLeft ? 'left' : 'right'}
          vAlignContent="center"
        >
          {value}
        </FlexView>
      </div>
    )
  } else {
    return (
      <div
        className="tw-relative tw-w-full tw-px-[15px]"
        style={{ background: color, marginLeft: margin }}
      >
        <FlexView
          hAlignContent={alignLeft ? 'left' : 'right'}
          vAlignContent="center"
        >
          {value}
        </FlexView>
      </div>
    )
  }
}

function renderWorkTimeSourceInfo(
  country: CountryModel,
  workTimeSource: number,
  translate: TranslateFn,
) {
  if (country.nationId != 17 || workTimeSource == null) return null

  let info = null
  if (workTimeSource == 0) {
    info = translate('foreignAmounts.eventsInfo')
  } else if (workTimeSource == 1) {
    info = translate('foreignAmounts.amplitudePartInfo')
  } else if (workTimeSource == 2) {
    info = translate('foreignAmounts.reducedAmplitudeInfo')
  }

  return <span style={{ fontSize: 11 }}>{info}</span>
}

function drawWorkWithAvailabilityInCountryRow(
  value1: string,
  value2: string,
  value3: string,
  value4: string,
  value5: string,
  bold: boolean = false,
  color: string = '#F8FAFC',
  margin: number = 2,
) {
  const isHeading = color !== '#F8FAFC'
  return (
    <tr>
      {renderCell(value1, { textAlign: 'left', heading: isHeading })}
      {renderCell(value2, { textAlign: 'left', heading: isHeading })}
      {renderCell(value3, { textAlign: 'left', heading: isHeading })}
      {renderCell(value4, { textAlign: 'left', heading: isHeading })}
      {renderCell(value5, { textAlign: 'left', heading: isHeading })}
    </tr>
  )
  return (
    <div
      className="tw-flex tw-flex-wrap -tw-mx-[15px]"
      style={{ marginTop: margin, fontWeight: bold ? 500 : 400 }}
    >
      {drawCell(value1, true, color, 0)}
      {drawCell(value2, false, color, margin)}
      {drawCell(value3, false, color, margin)}
      {drawCell(value4, false, color, margin)}
      {drawCell(value5, false, color, margin)}
    </div>
  )
}

type CellProps = {
  textAlign?: 'right' | 'left' | 'center'
  heading?: boolean
}

function renderCell(value: string | number, props?: CellProps) {
  const { textAlign, heading } = props
  return heading ? (
    <th style={{ textAlign }}>{value}</th>
  ) : (
    <td style={{ textAlign }}>{value}</td>
  )
}

function drawDetailsRow(
  value1: string,
  value2: string,
  value3: string,
  value4: string,
  value5: string,
  value6: string,
  value7: string,
  bold: boolean = false,
  color: string = '#F8FAFC',
  margin: number = 2,
) {
  const isHeading = color !== '#F8FAFC'
  return (
    <tr>
      {renderCell(value1, { textAlign: 'left', heading: isHeading })}
      {renderCell(value2, { textAlign: 'left', heading: isHeading })}
      {renderCell(value3, { textAlign: 'left', heading: isHeading })}
      {renderCell(value4, { textAlign: 'left', heading: isHeading })}
      {renderCell(value5, { textAlign: 'left', heading: isHeading })}
      {renderCell(value6, { textAlign: 'left', heading: isHeading })}
      {renderCell(value7, { textAlign: 'left', heading: isHeading })}
    </tr>
  )
  return (
    <div
      className="tw-flex tw-flex-wrap -tw-mx-[15px]"
      style={{ marginTop: margin, fontWeight: bold ? 500 : 400 }}
    >
      {drawCell(value1, true, color, 0, '3')}
      {drawCell(value2, false, color, margin, '1')}
      {drawCell(value3, false, color, margin, '1')}
      {drawCell(value4, false, color, margin, '1')}
      {drawCell(value5, false, color, margin)}
      {drawCell(value6, false, color, margin)}
      {drawCell(value7, false, color, margin)}
    </div>
  )
}
